import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Eye, User, Phone, CurrencyEur, Trophy, Star, CheckCircle, Clock, X,
} from "@phosphor-icons/react";

/**
 * VAGUE 32 — AdminTeleproDashboardView.
 *
 * Read-only mirror of any telepro's personal portal for Damien to spot-check
 * how they're doing without pinging them. Uses the accounts-health list as a
 * picker on the left, then a compact dashboard on the right.
 */
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch (_e) { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
}
function eur(n) { return `${Number(n || 0).toFixed(2)} €`; }

function KpiCard({ label, value, icon, testid }) {
  return (
    <div className="border border-border rounded-sm p-3 bg-card" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
        {icon}{label}
      </div>
      <div className="font-display text-lg font-medium">{value}</div>
    </div>
  );
}

function RecordChip({ label, value, date }) {
  return (
    <div className="text-xs bg-primary/5 border border-primary/20 rounded-sm px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="font-bold">
        {value > 0 ? <>{value} <span className="text-muted-foreground font-normal">({fmtDate(date)})</span></> : <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

export default function AdminTeleproDashboardView() {
  const [telepros, setTelepros] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      setListLoading(true);
      try {
        const r = await api.get("/admin/telepro/accounts-health");
        setTelepros(r.data?.items || []);
      } catch (e) {
        toast.error("Erreur chargement des télépros : " + e.message);
      } finally {
        setListLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) { setDash(null); return; }
    setLoading(true);
    api.get(`/admin/telepro/${selectedId}/dashboard-view`)
      .then((r) => setDash(r.data))
      .catch((e) => toast.error("Erreur : " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return telepros;
    return telepros.filter(t =>
      (t.code || "").toLowerCase().includes(q) ||
      (t.first_name || "").toLowerCase().includes(q) ||
      (t.last_name || "").toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q)
    );
  }, [telepros, filter]);

  return (
    <div className="border border-border rounded-sm bg-card" data-testid="admin-telepro-dashboard-view">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Eye size={18} weight="duotone" className="text-primary" />
        <h2 className="font-display text-base font-medium">Voir le dashboard d'un télépro (lecture seule)</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] min-h-[400px]">
        {/* LEFT — Picker */}
        <div className="border-r border-border bg-accent/20">
          <div className="p-3 border-b border-border">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrer par code, nom, email…"
              className="w-full text-xs border border-border rounded-sm px-2 py-1.5 bg-background outline-none focus:border-primary"
              data-testid="admin-view-filter"
            />
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {listLoading ? (
              <div className="p-4 text-xs text-muted-foreground">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground">Aucun télépro.</div>
            ) : filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-3 py-2 border-b border-border/60 hover:bg-primary/5 transition-colors ${selectedId === t.id ? "bg-primary/10" : ""}`}
                data-testid={`admin-view-pick-${t.code}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${t.has_password ? "bg-green-500" : "bg-orange-500"}`} title={t.has_password ? "Mot de passe OK" : "Pas encore de mot de passe"} />
                  <div className="font-mono font-bold text-xs">{t.code}</div>
                </div>
                <div className="text-[11px] text-muted-foreground">{t.first_name} {t.last_name}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {t.calls_7d || 0} appels/7j · {t.calls_30d || 0}/30j
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — Dashboard */}
        <div className="p-4">
          {!selectedId ? (
            <div className="h-full grid place-items-center text-xs text-muted-foreground text-center">
              ← Sélectionne un télépro pour voir son dashboard en lecture seule.
            </div>
          ) : loading ? (
            <div className="text-xs text-muted-foreground">Chargement…</div>
          ) : !dash ? (
            <div className="text-xs text-destructive">Impossible de charger.</div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                    <User size={12}/> Télépro
                  </div>
                  <div className="font-display text-lg font-medium">
                    {dash.telepro?.first_name} {dash.telepro?.last_name}
                    <span className="ml-2 font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{dash.telepro?.code}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{dash.telepro?.email} · Objectif quotidien : <b>{dash.telepro?.daily_goal || 40}</b></div>
                </div>
                <button
                  onClick={() => setSelectedId("")}
                  className="text-xs text-muted-foreground hover:text-foreground p-1"
                  aria-label="Fermer"
                  data-testid="admin-view-close"
                ><X size={16}/></button>
              </div>

              {/* Live pulse */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <KpiCard label="Appels aujourd'hui" value={dash.today_calls_count || 0} icon={<Phone size={12}/>} testid="admin-view-today-calls"/>
                <KpiCard label="Non traités" value={dash.untreated_today || 0} icon={<Clock size={12}/>} testid="admin-view-untreated"/>
                <KpiCard label="Ventes actives" value={dash.kpis?.sales_active || 0} icon={<CheckCircle size={12}/>} testid="admin-view-active-sales"/>
                <KpiCard label="Ce mois" value={eur(dash.kpis?.this_month_commissions_eur)} icon={<CurrencyEur size={12}/>} testid="admin-view-month"/>
              </div>

              {/* Cumul commissions */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <KpiCard label="Commis. ventes cumul" value={eur(dash.kpis?.total_sale_commissions_eur)} icon={<CurrencyEur size={12}/>} testid="admin-view-cumul-ventes"/>
                <KpiCard label="Commis. mensuelles cumul" value={eur(dash.kpis?.total_monthly_commissions_eur)} icon={<CurrencyEur size={12}/>} testid="admin-view-cumul-mens"/>
                <KpiCard label="Total reçu (all-time)" value={eur(dash.kpis?.grand_total_eur)} icon={<Trophy size={12}/>} testid="admin-view-total"/>
              </div>

              {/* Records */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                  <Trophy size={12}/> Records personnels
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <RecordChip label="Meilleur jour · appels" value={dash.kpis?.records?.best_calls_day?.value} date={dash.kpis?.records?.best_calls_day?.date}/>
                  <RecordChip label="Meilleur jour · ventes" value={dash.kpis?.records?.best_sales_day?.value} date={dash.kpis?.records?.best_sales_day?.date}/>
                  <RecordChip label="Meilleure semaine · ventes" value={dash.kpis?.records?.best_sales_week?.value} date={dash.kpis?.records?.best_sales_week?.week_start}/>
                </div>
              </div>

              {/* Today's list + last call */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-border rounded-sm p-3 bg-card">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Liste du jour</div>
                  {dash.today ? (
                    <div className="text-sm">
                      <b>{dash.today.count}</b> prospects · statut email : {dash.today.email_status || "—"}
                      <div className="text-xs text-muted-foreground mt-0.5">Date : {dash.today.date}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">Pas de liste générée aujourd'hui</div>
                  )}
                </div>
                <div className="border border-border rounded-sm p-3 bg-card">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Dernier appel</div>
                  {dash.last_call ? (
                    <div className="text-sm">
                      {fmtDateTime(dash.last_call.updated_at)}
                      <div className="text-xs text-muted-foreground">Statut : <b>{dash.last_call.status}</b></div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">Aucun appel enregistré</div>
                  )}
                </div>
              </div>

              {/* Recent sales */}
              {dash.recent_sales?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                    <Star size={12}/> Ventes récentes
                  </div>
                  <ul className="text-xs space-y-1">
                    {dash.recent_sales.slice(0, 6).map((s, i) => (
                      <li key={i} className="flex justify-between gap-2 border-b border-border/60 pb-1">
                        <span className="truncate">{s.business_name || s.business_id || "—"}</span>
                        <span className="text-muted-foreground shrink-0">{s.sale_date ? fmtDate(s.sale_date) : "—"} · {s.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
